import React from 'react';

import { IntlShape } from 'react-intl-next';

import {
  ACTION,
  ACTION_SUBJECT,
  ACTION_SUBJECT_ID,
  AnalyticsEventPayload,
  EditorAnalyticsAPI,
  EVENT_TYPE,
  INPUT_METHOD,
} from '@atlaskit/editor-common/analytics';
import {
  QuickInsertActionInsert,
  QuickInsertItem,
} from '@atlaskit/editor-common/provider-factory';
import {
  NextEditorPlugin,
  OptionalPlugin,
} from '@atlaskit/editor-common/types';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';
import { EditorState } from '@atlaskit/editor-prosemirror/state';

import CodeSuggestionsToolbarButton from './code-suggestions-toolbar-button';
import { codeSuggestionsEditModeMessages } from './code-suggestions.i18n';
import { CodeSuggestionsIcon } from './CodeSuggestionsIcon';
import {
  CODE_SUGGESTIONS_EXTENSION_KEY,
  CODE_SUGGESTIONS_EXTENSION_TYPE,
} from './constants';
import { registerEditorViewPlugin } from './pm-plugins/get-editor-view';
import { handlePMKeydownPlugin } from './pm-plugins/pm-key-handler';
import { insertCodeSuggestionNode } from './utils';

export const analyticsApiPayload = (
  inputMethod: INPUT_METHOD.QUICK_INSERT | INPUT_METHOD.TOOLBAR
): AnalyticsEventPayload => ({
  action: ACTION.INSERTED,
  actionSubject: ACTION_SUBJECT.DOCUMENT,
  actionSubjectId: ACTION_SUBJECT_ID.EXTENSION,
  attributes: {
    extensionKey: CODE_SUGGESTIONS_EXTENSION_KEY,
    extensionType: CODE_SUGGESTIONS_EXTENSION_TYPE,
    key: CODE_SUGGESTIONS_EXTENSION_KEY,
    inputMethod,
  },
  eventType: EVENT_TYPE.TRACK,
});

const codeSuggestionsQuickInsertItem = (
  intl: IntlShape,
  api: any
): QuickInsertItem => ({
  title: intl.formatMessage(codeSuggestionsEditModeMessages.title),
  description: intl.formatMessage(codeSuggestionsEditModeMessages.description),
  priority: 100,
  keywords: ['suggestion'],
  // keyshortcut: '```suggestion', we might need this later
  icon: () => <CodeSuggestionsIcon />,
  action: (_insert: QuickInsertActionInsert, state: EditorState, _source) => {
    const { tr } = state;
    const editorAnalyticsApi: EditorAnalyticsAPI | undefined =
      api?.analytics?.actions;
    editorAnalyticsApi?.attachAnalyticsEvent(
      analyticsApiPayload(INPUT_METHOD.QUICK_INSERT)
    )(tr);
    return insertCodeSuggestionNode(tr);
  },
});

export const codeSuggestionsPlugin: NextEditorPlugin<
  'codeSuggestions',
  {
    dependencies: [OptionalPlugin<AnalyticsPlugin>];
  }
> = ({ api }) => {
  return {
    name: 'codeSuggestions',
    pmPlugins: () => [
      {
        name: 'codeSuggestionsRegisterEditorView',
        plugin: () => registerEditorViewPlugin(),
      },
      {
        name: 'codeSuggestionsFocusPlugin',
        plugin: () => handlePMKeydownPlugin(),
      },
    ],
    primaryToolbarComponent: () => {
      return <CodeSuggestionsToolbarButton api={api} />;
    },
    pluginsOptions: {
      quickInsert: (intl: IntlShape) => [
        codeSuggestionsQuickInsertItem(intl, api),
      ],
    },
  };
};
